import { useState } from "react";
import { Hash, LogOut, Search, Shield, Bell, BellOff, Inbox } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "@/lib/chatApi";

const fmtTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return today
    ? d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" })
    : d.toLocaleDateString("id-ID", { day: "2-digit", month: "short" });
};

const preview = (m) => (m ? `${m.username}: ${m.text || (m.image_url ? "[Gambar]" : "")}` : "Belum ada pesan");

function DesktopNotifyButton() {
  const supported = typeof window !== "undefined" && "Notification" in window;
  const [perm, setPerm] = useState(supported ? Notification.permission : "denied");
  const ask = async () => {
    if (!supported) return toast.error("Browser tidak mendukung notifikasi desktop");
    const p = await Notification.requestPermission();
    setPerm(p);
    p === "granted" ? toast.success("Notifikasi desktop aktif") : toast.error("Izin notifikasi ditolak");
  };
  const on = perm === "granted";
  return (
    <button
      data-testid="admin-desktop-notify-button"
      onClick={ask}
      disabled={on}
      title={on ? "Notifikasi desktop aktif" : "Aktifkan notifikasi desktop"}
      className={`p-2 rounded-lg transition-colors ${on ? "text-emerald-500" : "text-slate-400 hover:text-amber-500 hover:bg-slate-200 dark:hover:bg-slate-800"}`}
    >
      {on ? <Bell className="w-4 h-4" /> : <BellOff className="w-4 h-4" />}
    </button>
  );
}

function InboxItem({ room, active, unread, last, onSelect }) {
  return (
    <button
      data-testid={`admin-room-item-${room.id}`}
      onClick={() => onSelect(room)}
      className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-colors border ${
        active
          ? "bg-amber-100 dark:bg-amber-500/15 border-amber-300 dark:border-amber-500/40"
          : "border-transparent hover:bg-slate-100 dark:hover:bg-slate-800/60"
      }`}
    >
      {room.logo ? (
        <img src={mediaUrl(room.logo)} alt={room.name} className="w-10 h-10 rounded-xl object-cover shrink-0 border border-slate-200 dark:border-slate-700" />
      ) : (
        <div className="w-10 h-10 rounded-xl bg-slate-200 dark:bg-slate-800 flex items-center justify-center shrink-0">
          <Hash className="w-4 h-4 text-slate-500" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className={`text-sm truncate flex-1 ${unread ? "font-bold text-slate-900 dark:text-white" : "font-semibold text-slate-700 dark:text-slate-200"}`}>{room.name}</p>
          <span className="text-[10px] text-slate-400 shrink-0" data-testid={`admin-room-time-${room.id}`}>{fmtTime(last?.created_at)}</span>
        </div>
        <div className="flex items-center gap-2 mt-0.5">
          <p className={`text-xs truncate flex-1 ${unread ? "text-slate-700 dark:text-slate-200" : "text-slate-400 dark:text-slate-500"}`} data-testid={`admin-room-preview-${room.id}`}>
            {preview(last)}
          </p>
          {!active && unread > 0 && (
            <span data-testid={`admin-unread-${room.id}`} className="min-w-5 h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center shrink-0">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}

export default function AdminInboxSidebar({ rooms, activeRoom, unread = {}, lastByRoom = {}, onSelect, user, onLogout }) {
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all");
  const isUnread = (r) => r.id !== activeRoom?.id && (unread[r.id] || 0) > 0;
  const unreadCount = rooms.filter(isUnread).length;
  const [stickyId, setStickyId] = useState(null);
  const sorted = rooms
    .filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
    .filter((r) => tab === "all" || isUnread(r) || r.id === stickyId)
    .sort((a, b) => ((lastByRoom[b.id]?.created_at || "") > (lastByRoom[a.id]?.created_at || "") ? 1 : -1));

  return (
    <div data-testid="admin-inbox-sidebar" className="w-full md:w-80 border-r border-slate-200 dark:border-slate-800/80 bg-white/80 dark:bg-slate-900/60 backdrop-blur-xl flex flex-col h-full shrink-0">
      <div className="h-16 px-4 flex items-center gap-2.5 border-b border-slate-200 dark:border-slate-800/80 shrink-0">
        <div className="w-9 h-9 rounded-xl bg-amber-500 flex items-center justify-center">
          <Inbox className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-base font-extrabold text-slate-900 dark:text-white font-heading leading-tight">Inbox Admin</p>
          <p className="text-[11px] text-slate-400" data-testid="admin-room-count">{rooms.length} room ditangani</p>
        </div>
        <DesktopNotifyButton />
      </div>
      <div className="p-3 space-y-3">
        <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/60" data-testid="admin-inbox-tabs">
          {[["all", "Semua"], ["unread", "Belum dibaca"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`admin-tab-${key}`}
              onClick={() => { setTab(key); setStickyId(null); }}
              className={`flex items-center justify-center gap-1.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                tab === key ? "bg-white dark:bg-slate-900 text-amber-600 dark:text-amber-400 shadow-sm" : "text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"
              }`}
            >
              {label}
              {key === "unread" && unreadCount > 0 && (
                <span data-testid="admin-unread-tab-count" className="min-w-4 h-4 px-1 rounded-full bg-amber-500 text-white text-[9px] font-bold flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            data-testid="admin-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cari room..."
            className="w-full pl-9 pr-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 text-xs text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-amber-500"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-3 space-y-1" data-testid="admin-rooms-list">
        {sorted.length === 0 && (
          <p className="text-xs text-slate-400 text-center py-8" data-testid="admin-no-rooms">
            {tab === "unread" ? "Semua pesan sudah dibaca." : "Belum ada room yang ditetapkan untuk Anda."}
          </p>
        )}
        {sorted.map((r) => (
          <InboxItem key={r.id} room={r} active={activeRoom?.id === r.id} unread={unread[r.id] || 0} last={lastByRoom[r.id]} onSelect={(room) => { if (tab === "unread") setStickyId(room.id); onSelect(room); }} />
        ))}
      </div>
      <div className="p-3 border-t border-slate-200 dark:border-slate-800/80 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center">
          <Shield className="w-4 h-4 text-amber-500" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900 dark:text-white truncate" data-testid="admin-username">{user.username}</p>
          <p className="text-xs text-amber-600 dark:text-amber-400">{user.isModerator ? "Moderator global" : "Admin room"}</p>
        </div>
        <button data-testid="admin-logout-button" onClick={onLogout} className="p-2 rounded-lg text-slate-400 hover:text-red-500 hover:bg-slate-200 dark:hover:bg-slate-800 transition-colors">
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
